/**
 * GOVIA UI V2 — Motion tokens (Fase 1).
 *
 * NUEVO (no existía un archivo centralizado de motion) — pero los valores
 * de duración (180ms) ya son el estándar de facto en `theme/components.ts`
 * (hover de botones/chips/inputs, Fase B) — este archivo centraliza y
 * documenta ese rango en vez de contradecirlo. Rango pedido: 120-240ms.
 */
export const motion = {
  duration: {
    fast: 120,
    base: 180,
    slow: 240,
  },
  // Forma CSS (para `sx.transition`, ej. Sidebar/CommandSurface) y forma
  // array (para el prop `ease` de framer-motion, que no acepta strings
  // cubic-bezier arbitrarios) — misma curva Material "standard" en ambas.
  easing: {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
    accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
  },
  easingArray: {
    standard: [0.4, 0, 0.2, 1],
    decelerate: [0, 0, 0.2, 1],
    accelerate: [0.4, 0, 1, 1],
  },
} as const

/** Variants de framer-motion listos para hover/focus/entrance/reveal — el
 * llamador decide cuál aplica, nunca animación constante/decorativa. */
export const motionVariants = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: motion.duration.base / 1000, ease: motion.easingArray.standard },
  },
  slideUp: {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 },
    transition: { duration: motion.duration.base / 1000, ease: motion.easingArray.standard },
  },
  reveal: {
    initial: { opacity: 0, scale: 0.98 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: motion.duration.slow / 1000, ease: motion.easingArray.decelerate },
  },
  drawer: {
    initial: { opacity: 0, x: 24 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 24 },
    transition: { duration: motion.duration.base / 1000, ease: motion.easingArray.standard },
  },
} as const

/** `prefers-reduced-motion` — cualquier componente V2 que anime debe
 * consultarlo y, si está activo, saltar directo al estado final (sin
 * transición) en vez de ignorarlo. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}
